import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	TextField,
	Button,
	Avatar,
	useTheme,
	useMediaQuery,
	CircularProgress,
	Link,
	Divider,
	IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useTranslation } from '../../hooks/useTranslation';

function LinkAccountsOtp({
	fipId,
	fipName,
	fipLogo,
	accounts,
	onLinkSuccess,
	onSkip,
	onCancel,
	authApis,
	customDetails,
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const [otp, setOtp] = useState('');
	const [isLinking, setIsLinking] = useState(false);
	const [isInitiating, setIsInitiating] = useState(true);
	const [linkingRefNumber, setLinkingRefNumber] = useState('');
	const [resendTimer, setResendTimer] = useState(60);
	const [canResend, setCanResend] = useState(false);
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	// Initiate linking on component mount
	useEffect(() => {
		initiateAccountLinking();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (resendTimer > 0 && linkingRefNumber) {
			const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
			return () => clearTimeout(timer);
		} else if (linkingRefNumber) {
			setCanResend(true);
		}
	}, [resendTimer, linkingRefNumber]);

	const initiateAccountLinking = async () => {
		setIsInitiating(true);
		try {
			// Remove restrictions, isLinked, fipId, and fipName from accounts before linking
			const accountsToLink = accounts.map(
				({
					restrictions,
					isLinked,
					fipId: _,
					fipName: __,
					...accountWithoutExtra
				}) => accountWithoutExtra
			);

			const res = await authApis.accountLinking(fipId, accountsToLink);

			if (!res) {
				toast.error(t('errors.general.somethingWentWrong'));
				onCancel();
				return;
			}

			if (res.status === 'SUCCESS') {
				setLinkingRefNumber(res.RefNumber);
				setResendTimer(60);
				setCanResend(false);
			} else {
				if (res?.code === undefined || res?.code === null || res?.code === '') {
					toast.error(res?.message);
				} else {
					toast.error(t(`errors.code.${res.code}`));
				}
				onCancel();
			}
		} catch (error) {
			console.error('Initiate linking error:', error);
			toast.error(t('errors.general.failedToLink'));
			onCancel();
		} finally {
			setIsInitiating(false);
		}
	};

	const handleOtpChange = (e) => {
		setOtp(e.target.value);
	};

	const handleResendOtp = async () => {
		setCanResend(false);
		setOtp('');
		await initiateAccountLinking();
	};

	const handleLinkAccounts = async () => {
		if (
			!otp ||
			!(otp.length === 4 || otp.length === 6 || otp.length === 8) ||
			!linkingRefNumber
		) {
			return;
		}

		setIsLinking(true);
		try {
			const res = await authApis.accountConfirmLinking(
				linkingRefNumber,
				otp,
				fipId
			);

			if (!res) {
				toast.error(t('errors.general.somethingWentWrong'));
				return;
			}

			if (res.status === 'SUCCESS') {
				toast.success(res.message || t('linkAccounts.messages.linkSuccess'));

				// Map the linked accounts with their linkRefNumber from the API response
				const linkedAccountsWithRefNumber = accounts.map((account) => {
					const linkedDetail = res?.AccLinkDetails?.find(
						(detail) => detail.accRefNumber === account.accRefNumber
					);
					return {
						...account,
						linkRefNumber:
							linkedDetail?.linkRefNumber || linkedDetail?.RefNumber,
						fipId: fipId,
						fipName: fipName,
					};
				});

				onLinkSuccess(linkedAccountsWithRefNumber);
			} else {
				setOtp('');
				if (res?.code === undefined || res?.code === null || res?.code === '') {
					toast.error(res?.message);
				} else {
					toast.error(t(`errors.code.${res.code}`));
				}
			}
		} catch (error) {
			console.error('Link accounts error:', error);
			toast.error(t('errors.general.failedToLink'));
		} finally {
			setIsLinking(false);
		}
	};

	// Show loading state while initiating
	if (isInitiating) {
		return (
			<Box
				sx={{
					p: mobileScreen ? '1.5rem' : '1.2rem',
					maxWidth: mobileScreen ? '100%' : '540px',
					width: '100%',
					minHeight: '180px',
				}}
			>
				{/* Close button - only for desktop modal */}
				{!mobileScreen && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							mb: '1rem',
						}}
					>
						<IconButton
							onClick={onCancel}
							sx={{
								color: isDark ? '#FFFFFF' : '#000000',
								padding: '4px',
							}}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				)}

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '120px',
					}}
				>
					<CircularProgress size={48} sx={{ mb: '1rem' }} />
					<Typography
						sx={{
							fontSize: '1rem',
							color: isDark ? '#B0B0B0' : '#5F6368',
						}}
					>
						{t('linkAccounts.messages.initiating')}
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				p: mobileScreen ? '1.5rem' : '1.2rem',
				maxWidth: mobileScreen ? '100%' : '540px',
				width: '100%',
				position: 'relative',
			}}
		>
			{/* FIP Logo and Close button - horizontally aligned */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: '1rem',
				}}
			>
				<Avatar
					src={fipLogo || global.objectFormatFip?.[fipId]?.productIconUri}
					alt={fipName}
					sx={{
						width: 32,
						height: 32,
						border: `1px solid ${isDark ? '#404040' : '#E0E0E0'}`,
					}}
				/>
				{!mobileScreen && (
					<IconButton
						onClick={onCancel}
						sx={{
							color: isDark ? '#FFFFFF' : '#000000',
							padding: '4px',
						}}
					>
						<CloseIcon />
					</IconButton>
				)}
			</Box>

			{/* Title */}
			<Typography
				sx={{
					fontSize: '1.25rem',
					fontWeight: 700,
					color: isDark ? '#FFFFFF' : '#000000',
					mb: '1.1rem',
				}}
			>
				{t('linkAccounts.titles.linkYourAccounts').replace(
					'{{fipName}}',
					fipName
				)}
			</Typography>

			{/* Description */}
			<Typography
				sx={{
					fontSize: '0.875rem',
					color: isDark ? '#B0B0B0' : '#5F6368',
					mb: '1.1rem',
					lineHeight: 1.5,
				}}
			>
				{t('linkAccounts.messages.verifyAndLink')}
			</Typography>

			{/* OTP Input */}
			<TextField
				placeholder={t('common.placeholders.enterOtp')}
				value={otp}
				onChange={handleOtpChange}
				disabled={isLinking}
				sx={{
					width: '60%',
					mb: '0.7rem',
					'& .MuiOutlinedInput-root': {
						fontSize: '1rem',
						backgroundColor: isDark ? '#2C2C2C' : '#F8F9FA',
						borderRadius: '8px',
						'& fieldset': {
							borderColor: isDark ? '#404040' : '#E0E0E0',
							borderRadius: '8px',
						},
						'&:hover fieldset': {
							borderColor: theme.palette.primary.main,
						},
						'&.Mui-focused fieldset': {
							borderColor: theme.palette.primary.main,
						},
					},
					'& input': {
						color: isDark ? '#FFFFFF' : '#000000',
					},
				}}
			/>

			{/* Resend OTP */}
			<Box sx={{ mb: '1.5rem', textAlign: 'left' }}>
				{canResend ? (
					<Link
						component="button"
						onClick={handleResendOtp}
						disabled={isLinking}
						sx={{
							textDecoration: 'none',
							color: theme.palette.primary.main,
							fontSize: '0.875rem',
							cursor: 'pointer',
							'&:hover': {
								textDecoration: 'underline',
							},
						}}
					>
						{t('linkAccounts.buttons.resendOtp')}
					</Link>
				) : (
					<Typography
						sx={{
							fontSize: '0.875rem',
							color: isDark ? '#7A8185' : '#5F6368',
						}}
					>
						{t('linkAccounts.messages.resendOtpIn').replace(
							'{{seconds}}',
							resendTimer
						)}
					</Typography>
				)}
			</Box>

			{/* Divider */}
			<Divider
				sx={{
					mb: '1rem',
					borderColor: isDark ? '#404040' : '#E0E0E0',
					mx: mobileScreen ? '-1.5rem' : '-1.2rem',
					width: mobileScreen ? 'calc(100% + 3rem)' : 'calc(100% + 2.4rem)',
				}}
			/>

			{/* Action Buttons */}
			<Box
				sx={{
					display: 'flex',
					gap: '1rem',
					justifyContent: 'flex-end',
					flexDirection: mobileScreen ? 'column' : 'row',
				}}
			>
				<Button
					onClick={handleLinkAccounts}
					disabled={
						!otp ||
						!(otp.length === 4 || otp.length === 6 || otp.length === 8) ||
						isLinking
					}
					sx={{
						textTransform: 'none',
						fontSize: '0.95rem',
						fontWeight: 600,
						padding: '0.55rem 1.65rem',
						borderRadius: '8px',
						backgroundColor:
							customDetails?.primaryColor || theme.palette.primary.main,
						color: '#FFFFFF',
						'&:hover': {
							backgroundColor:
								customDetails?.primaryColor || theme.palette.primary.dark,
						},
						'&:disabled': {
							backgroundColor: isDark ? '#2C2C2C' : '#E0E0E0',
							color: isDark ? '#7A8185' : '#9E9E9E',
						},
					}}
				>
					{isLinking
						? t('linkAccounts.buttons.linking')
						: t('linkAccounts.buttons.linkAccounts')}
				</Button>
				<Button
					onClick={onSkip}
					disabled={isLinking}
					sx={{
						textTransform: 'none',
						fontSize: '0.95rem',
						fontWeight: 600,
						padding: '0.55rem 1.65rem',
						borderRadius: '8px',
						border: `1px solid ${isDark ? '#7A8185' : '#9E9E9E'}`,
						color: isDark ? '#B0B0B0' : '#5F6368',
						backgroundColor: 'transparent',
						'&:hover': {
							backgroundColor: isDark
								? 'rgba(122, 129, 133, 0.1)'
								: 'rgba(158, 158, 158, 0.05)',
						},
					}}
				>
					{t('common.buttons.cancel')}
				</Button>
			</Box>
		</Box>
	);
}

export default LinkAccountsOtp;
